import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { landlordApi } from '../../services/api';

interface Invoice {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  status: 'draft' | 'sent' | 'pending' | 'paid' | 'overdue' | 'cancelled';
  createdAt: string;
  dueAt?: string;
  paidAt?: string;
  fastSpringId?: string;
  fastSpringUrl?: string;
}

interface PaymentStatusProps {
  order?: any;
}

export default function PaymentStatus({ order: propOrder }: PaymentStatusProps = {}) {
  const { orderId: urlOrderId } = useParams<{ orderId: string }>();
  const orderId = propOrder?.id || urlOrderId;
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openingPayment, setOpeningPayment] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadInvoice();
    }
  }, [orderId]);

  const loadInvoice = async () => {
    try {
      setError(null);
      setLoading(true);

      const response = await landlordApi.get(`api/orders/${orderId}/invoice`);
      setInvoice(response.data.data || response.data);
    } catch (err: any) {
      console.error('[PaymentStatus] Load failed:', err);
      if (err?.response?.status === 404) {
        setError('Invoice not found for this order. Payment may not be required yet.');
      } else {
        setError(
          err instanceof Error ? err.message : 'Failed to load payment information'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'yellow';
      case 'overdue':
        return 'red';
      case 'sent':
        return 'blue';
      case 'draft':
        return 'gray';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return '✓';
      case 'pending':
        return '⏳';
      case 'overdue':
        return '⚠';
      case 'sent':
        return '📧';
      case 'draft':
        return '📋';
      case 'cancelled':
        return '✕';
      default:
        return '○';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handlePayNow = async () => {
    if (!invoice?.fastSpringUrl) {
      // eslint-disable-next-line no-restricted-globals
      alert('Payment link not available');
      return;
    }

    setOpeningPayment(true);
    window.open(invoice.fastSpringUrl, '_blank');
    setOpeningPayment(false);
  };

  const isDaysUntilDue = (dueAt?: string) => {
    if (!dueAt) return null;
    const days = Math.ceil(
      (new Date(dueAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-12 w-12 bg-emerald-200 rounded-full mb-4"></div>
          <p className="text-gray-600">Loading payment information...</p>
        </div>
      </div>
    );
  }

  if (!invoice || error) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <div className="text-4xl mb-3">💳</div>
        <p className="text-amber-800 font-medium mb-2">
          {error || 'No invoice available yet'}
        </p>
        <p className="text-amber-700 text-sm mb-6">
          The partner may contact you separately to arrange payment, or an invoice will be generated once work begins.
        </p>
        <button
          onClick={() => navigate(`/certificate-orders/${orderId}`)}
          className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 font-medium"
        >
          Back to Order
        </button>
      </div>
    );
  }

  const statusColor = getStatusColor(invoice.status);
  const daysUntilDue = isDaysUntilDue(invoice.dueAt);
  const isOverdue = daysUntilDue !== null && daysUntilDue < 0;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Main Invoice Card */}
      <div className={`bg-white rounded-lg shadow-sm border-2 border-${statusColor}-200 p-4 md:p-8 mb-6`}>
        {/* Invoice Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-8 border-b border-gray-200">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Invoice</h1>
            <p className="text-gray-600 text-sm mt-1">Invoice #{invoice.id}</p>
          </div>
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-white bg-${statusColor}-600`}
          >
            <span>{getStatusIcon(invoice.status)}</span>
            <span>{invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}</span>
          </div>
        </div>

        {/* Amount */}
        <div className="mb-8 p-6 bg-gradient-to-br from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-gray-600 font-medium mb-2">Amount Due</p>
          <p className="text-4xl font-bold text-emerald-700">
            {formatCurrency(invoice.amount, invoice.currency)}
          </p>
          <p className="text-xs text-gray-600 mt-2">Currency: {invoice.currency}</p>
        </div>

        {/* Invoice Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 p-6 bg-gray-50 rounded-lg">
          <div>
            <p className="text-xs text-gray-600 font-medium uppercase mb-1">Issued</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatDate(invoice.createdAt)}
            </p>
          </div>

          {invoice.dueAt && (
            <div>
              <p className="text-xs text-gray-600 font-medium uppercase mb-1">Due</p>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(invoice.dueAt)}
                </p>
                {daysUntilDue !== null && (
                  <p
                    className={`text-xs mt-1 font-medium ${
                      isOverdue ? 'text-red-600' : 'text-emerald-600'
                    }`}
                  >
                    {isOverdue
                      ? `${Math.abs(daysUntilDue || 0)} days overdue`
                      : daysUntilDue === 0
                        ? 'Due today'
                        : `${daysUntilDue} days remaining`}
                  </p>
                )}
              </div>
            </div>
          )}

          {invoice.paidAt && (
            <div>
              <p className="text-xs text-gray-600 font-medium uppercase mb-1">Paid</p>
              <p className="text-lg font-semibold text-green-600">
                {formatDate(invoice.paidAt)}
              </p>
            </div>
          )}

          {invoice.fastSpringId && (
            <div>
              <p className="text-xs text-gray-600 font-medium uppercase mb-1">
                FastSpring Reference
              </p>
              <p className="text-sm font-mono text-gray-700">{invoice.fastSpringId}</p>
            </div>
          )}
        </div>

        {/* Status Message */}
        {invoice.status === 'paid' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium flex items-center gap-2">
              <span>✓</span>
              Payment received on {formatDate(invoice.paidAt || new Date().toISOString())}
            </p>
          </div>
        )}

        {(invoice.status === 'pending' || invoice.status === 'sent') && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium mb-3">Payment required</p>
            <p className="text-sm text-blue-700 mb-4">
              Please complete payment using the secure FastSpring payment link below.
            </p>
            <button
              onClick={handlePayNow}
              disabled={openingPayment || !invoice.fastSpringUrl}
              className={`w-full px-4 py-3 rounded-lg font-medium text-white transition ${
                openingPayment || !invoice.fastSpringUrl
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {openingPayment ? '⏳ Opening Payment...' : '💳 Pay Now with FastSpring'}
            </button>
          </div>
        )}

        {invoice.status === 'overdue' && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium mb-3">⚠ Invoice is overdue</p>
            <p className="text-sm text-red-700 mb-4">
              This invoice is{' '}
              {daysUntilDue && Math.abs(daysUntilDue) === 1
                ? 'overdue by 1 day'
                : `overdue by ${Math.abs(daysUntilDue || 0)} days`}
              . Please complete payment immediately.
            </p>
            {invoice.fastSpringUrl && (
              <button
                onClick={handlePayNow}
                disabled={openingPayment}
                className={`w-full px-4 py-3 rounded-lg font-medium text-white transition ${
                  openingPayment
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {openingPayment ? '⏳ Opening Payment...' : '💳 Pay Now'}
              </button>
            )}
          </div>
        )}

        {invoice.status === 'draft' && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-gray-700 text-sm">
              This invoice is still in draft status. Payment will be requested once it's finalized.
            </p>
          </div>
        )}

        {invoice.status === 'cancelled' && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-gray-700 text-sm">This invoice has been cancelled.</p>
          </div>
        )}
      </div>

      {/* FastSpring Payment Info */}
      {invoice.fastSpringUrl && invoice.status !== 'paid' && invoice.status !== 'draft' && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <p className="text-emerald-900 text-sm flex items-start gap-2">
            <span className="text-lg flex-shrink-0">🔒</span>
            <span>
              Payment is securely processed by FastSpring. Your payment details are never shared with
              Landlord Pro.
            </span>
          </p>
        </div>
      )}

      {/* Back Button */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={() => navigate(`/certificate-orders/${orderId}`)}
          className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-900 hover:bg-gray-50 font-medium transition"
        >
          Back to Order
        </button>
        <button
          onClick={loadInvoice}
          className="flex-1 px-4 py-3 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium transition"
        >
          🔄 Refresh Status
        </button>
      </div>
    </div>
  );
}
